import React, {useEffect, useRef, useState} from 'react';
import {Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Select, useMediaQuery} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import PropTypes from 'prop-types';
import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import SoftInput from "../../../components/SoftInput";
import SoftButton from "../../../components/SoftButton";
import {
    formatMalaysianPhone,
    validateMalaysianPhone,
    validateRequired,
    validateUrl
} from "../../../utils/formValidations";
import {handleApiError, showCreateSuccess, showFormErrors, showUpdateSuccess} from "../../../utils/notificationUtils";
import {COMPANY_STATUS_OPTIONS, USER_STATUS_SELECT_OPTIONS} from "../../../utils/formConstants";
import {useRayzayUIController} from "../../../context";

const initialFormState = {
    name: '',
    description: '',
    address: '',
    webUrl: '',
    phoneNo: '',
    maxNoOfBranches: '',
    status: COMPANY_STATUS_OPTIONS.ACTIVE,
};

const AddEditCompanyDialog = ({open, onClose, company, isEdit, onSubmit}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [controller] = useRayzayUIController();
    const {loggedInUserDetail} = controller;
    const isCompanyAdmin = loggedInUserDetail?.userRole === "COMPANY_ADMIN";
    const [formData, setFormData] = useState(initialFormState);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isMountedRef = useRef(true);

    // Effect to populate form when editing
    useEffect(() => {
        if (!isMountedRef.current) return;

        console.log('Dialog useEffect triggered:', { 
            open, 
            isEdit, 
            hasCompany: !!company,
            companyId: company?.companyId,
            companyName: company?.name 
        });

        if (open && isEdit && company) {
            // Populate form with company data when editing
            console.log('Loading company data for edit - Full company object:', company);
            const newFormData = {
                name: company.name || '',
                description: company.description || '',
                address: company.address || '',
                webUrl: (company.webUrl && company.webUrl !== '-' && company.webUrl.trim() !== '') ? company.webUrl : '',
                phoneNo: company.phoneNo || '',
                maxNoOfBranches: (company.maxNoOfBranches !== null && company.maxNoOfBranches !== undefined && company.maxNoOfBranches !== '-') ? company.maxNoOfBranches : '',
                status: company.status || COMPANY_STATUS_OPTIONS.ACTIVE,
            };
            console.log('Setting form data to:', newFormData);
            setFormData(newFormData);
        }
    }, [isEdit, company, open]);

    // Effect to reset form when adding new company
    useEffect(() => {
        if (!isMountedRef.current) return;
        
        if (open && !isEdit) {
            console.log('Resetting form for new company');
            setFormData(initialFormState);
        }
    }, [open, isEdit]);

    // Effect to reset errors when dialog opens
    useEffect(() => {
        if (open) {
            setIsSubmitting(false);
            setError(null);
            setErrors({});
        }
    }, [open]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleChange = (e) => {
        const {name, value} = e.target;
        // Handle number input for maxNoOfBranches
        const processedValue = name === 'maxNoOfBranches' 
            ? (value === '' ? '' : parseInt(value, 10) || '')
            : value;
        setFormData(prev => ({...prev, [name]: processedValue}));
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: null}));
        }
    };

    const handlePhoneChange = (e) => {
        const formattedPhone = formatMalaysianPhone(e.target.value);
        setFormData(prev => ({
            ...prev,
            phoneNo: formattedPhone
        }));
        // Clear error when user starts typing
        if (errors.phoneNo) {
            setErrors(prev => ({...prev, phoneNo: null}));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Validate company name (required)
        const nameError = validateRequired(formData.name, 'Company name');
        if (nameError) {
            newErrors.name = nameError;
        }

        // Validate phone number using Malaysian phone validation
        const phoneError = validateMalaysianPhone(formData.phoneNo);
        if (phoneError) {
            newErrors.phoneNo = phoneError;
        }

        // Validate address (required)
        const addressError = validateRequired(formData.address, 'Address');
        if (addressError) {
            newErrors.address = addressError;
        }

        // Validate max no of branches (required only if not COMPANY_ADMIN in edit mode)
        if (!(isCompanyAdmin && isEdit)) {
            const maxBranchesError = validateRequired(formData.maxNoOfBranches, 'Maximum number of branches');
            if (maxBranchesError) {
                newErrors.maxNoOfBranches = maxBranchesError;
            }
        }

        // Validate status (required only if not COMPANY_ADMIN in edit mode)
        if (!(isCompanyAdmin && isEdit)) {
            if (!formData.status) {
                newErrors.status = 'Status is required';
            }
        }

        // Validate web URL (optional but validate if provided)
        if (formData.webUrl && formData.webUrl.trim() !== '') {
            const urlError = validateUrl(formData.webUrl);
            if (urlError) {
                newErrors.webUrl = urlError;
            }
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) {
            return; // Prevent double submission
        }

        // Clear previous errors
        setErrors({});
        setError(null);

        // Validate form
        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            showFormErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);

        try {
            // Prepare form data for submission
            const submitData = {
                ...formData,
                webUrl: formData.webUrl && formData.webUrl.trim() !== '' ? formData.webUrl.trim() : null,
                // For COMPANY_ADMIN in edit mode, preserve original values for maxNoOfBranches and status
                maxNoOfBranches: (isCompanyAdmin && isEdit) 
                    ? company.maxNoOfBranches 
                    : (formData.maxNoOfBranches === '' ? null : (typeof formData.maxNoOfBranches === 'string' ? parseInt(formData.maxNoOfBranches, 10) : formData.maxNoOfBranches)),
                status: (isCompanyAdmin && isEdit) 
                    ? company.status 
                    : formData.status
            };
            await onSubmit(submitData);
            if (isMountedRef.current) {
                if (isEdit) {
                    showUpdateSuccess('Company updated successfully');
                } else {
                    showCreateSuccess('Company created successfully');
                }
                onClose();
            }
        } catch (err) {
            console.error('Error submitting company:', err);
            if (isMountedRef.current) {
                const errorMessage = handleApiError(err, 'Failed to save company. Please try again.');
                setError(errorMessage);
            }
        } finally {
            if (isMountedRef.current) {
                setIsSubmitting(false);
            }
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setFormData(initialFormState);
            setErrors({});
            setError(null);
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
            <DialogTitle>
                <SoftTypography variant="h5" fontWeight="bold">
                    {isEdit ? 'Edit Company' : 'Add New Company'}
                </SoftTypography>
            </DialogTitle>
            <DialogContent>
                <SoftBox component="form" onSubmit={handleSubmit} pt={2}>
                    {error && (
                        <SoftBox mb={2}>
                            <SoftTypography variant="body2" color="error">
                                {error}
                            </SoftTypography>
                        </SoftBox>
                    )}

                    <Grid container spacing={2}>
                        {isEdit && company?.referenceNumber && (
                            <Grid item xs={12}>
                                <SoftBox mb={2}>
                                    <SoftTypography variant="caption" fontWeight="bold" mb={0.5}>
                                        Reference Number
                                    </SoftTypography>
                                    <SoftInput
                                        type="text"
                                        name="referenceNumber"
                                        value={company.referenceNumber || ''}
                                        disabled
                                        fullWidth
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                            }
                                        }}
                                    />
                                    <SoftTypography variant="caption" color="text" mt={0.5} sx={{fontStyle: 'italic'}}>
                                        This field is auto-generated and cannot be changed
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <SoftBox mb={2}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={0.5}>
                                    Company Name *
                                </SoftTypography>
                                <SoftInput
                                    type="text"
                                    name="name"
                                    placeholder="Enter company name"
                                    value={formData.name || ''}
                                    onChange={handleChange}
                                    error={!!errors.name}
                                    fullWidth
                                />
                                {errors.name && (
                                    <SoftTypography variant="caption" color="error" mt={0.5}>
                                        {errors.name}
                                    </SoftTypography>
                                )}
                            </SoftBox>
                        </Grid>

                        <Grid item xs={12}>
                            <SoftBox mb={2}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={0.5}>
                                    Description
                                </SoftTypography>
                                <SoftInput
                                    type="text"
                                    name="description"
                                    placeholder="Enter company description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    multiline
                                    rows={3}
                                    fullWidth
                                />
                            </SoftBox>
                        </Grid>

                        <Grid item xs={12}>
                            <SoftBox mb={2}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={0.5}>
                                    Address *
                                </SoftTypography>
                                <SoftInput
                                    type="text"
                                    name="address"
                                    placeholder="Enter company address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    error={!!errors.address}
                                    multiline
                                    rows={3}
                                    fullWidth
                                />
                                {errors.address && (
                                    <SoftTypography variant="caption" color="error" mt={0.5}>
                                        {errors.address}
                                    </SoftTypography>
                                )}
                            </SoftBox>
                        </Grid>

                        <Grid item xs={12}>
                            <SoftBox mb={2}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={0.5}>
                                    Website URL
                                </SoftTypography>
                                <SoftInput
                                    type="text"
                                    name="webUrl"
                                    placeholder="https://example.com"
                                    value={formData.webUrl}
                                    onChange={handleChange}
                                    error={!!errors.webUrl}
                                    fullWidth
                                />
                                {errors.webUrl && (
                                    <SoftTypography variant="caption" color="error" mt={0.5}>
                                        {errors.webUrl}
                                    </SoftTypography>
                                )}
                            </SoftBox>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={2}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={0.5}>
                                    Phone Number *
                                </SoftTypography>
                                <SoftInput
                                    type="text"
                                    name="phoneNo"
                                    placeholder="Enter phone number in E.164 format (e.g., +60133034946)"
                                    value={formData.phoneNo}
                                    onChange={handlePhoneChange}
                                    error={!!errors.phoneNo}
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderColor: errors.phoneNo ? theme.palette.error.main : theme.palette.grey[400],
                                        },
                                        '& .MuiOutlinedInput-root:hover': {
                                            borderColor: errors.phoneNo ? theme.palette.error.main : theme.palette.primary.main,
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused': {
                                            borderColor: errors.phoneNo ? theme.palette.error.main : theme.palette.primary.main,
                                        },
                                    }}
                                />
                                {errors.phoneNo && (
                                    <SoftTypography variant="caption" color="error" mt={0.5}>
                                        {errors.phoneNo}
                                    </SoftTypography>
                                )}
                            </SoftBox>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={2}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={0.5}>
                                    Maximum Number of Branches {!(isCompanyAdmin && isEdit) && '*'}
                                </SoftTypography>
                                <SoftInput
                                    type="number"
                                    name="maxNoOfBranches"
                                    placeholder="Enter maximum number of branches"
                                    value={formData.maxNoOfBranches}
                                    onChange={handleChange}
                                    error={!!errors.maxNoOfBranches}
                                    disabled={isCompanyAdmin && isEdit}
                                    fullWidth
                                />
                                {errors.maxNoOfBranches && (
                                    <SoftTypography variant="caption" color="error" mt={0.5}>
                                        {errors.maxNoOfBranches}
                                    </SoftTypography>
                                )}
                                {isCompanyAdmin && isEdit && (
                                    <SoftTypography variant="caption" color="text.secondary" mt={0.5}>
                                        This field cannot be modified by company admin
                                    </SoftTypography>
                                )}
                            </SoftBox>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={2}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={0.5}>
                                    Status {!(isCompanyAdmin && isEdit) && '*'}
                                </SoftTypography>
                                <Select
                                    name="status"
                                    value={formData.status || COMPANY_STATUS_OPTIONS.ACTIVE}
                                    onChange={handleChange}
                                    displayEmpty
                                    disabled={isCompanyAdmin && isEdit}
                                    fullWidth
                                    sx={{
                                        '& .MuiSelect-select': {
                                            padding: '10px 14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: errors.status ? theme.palette.error.main : theme.palette.grey[400],
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: errors.status ? theme.palette.error.main : theme.palette.primary.main,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: errors.status ? theme.palette.error.main : theme.palette.primary.main,
                                        },
                                    }}
                                >
                                    {USER_STATUS_SELECT_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.status && (
                                    <SoftTypography variant="caption" color="error" mt={0.5}>
                                        {errors.status}
                                    </SoftTypography>
                                )}
                                {isCompanyAdmin && isEdit && (
                                    <SoftTypography variant="caption" color="text.secondary" mt={0.5}>
                                        This field cannot be modified by company admin
                                    </SoftTypography>
                                )}
                            </SoftBox>
                        </Grid>
                    </Grid>
                </SoftBox>
            </DialogContent>
            <DialogActions>
                <SoftButton
                    variant="text"
                    color="text"
                    onClick={handleClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </SoftButton>
                <SoftButton
                    variant="gradient"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={
                        isSubmitting || 
                        !formData.name || 
                        formData.name.trim() === '' ||
                        !formData.phoneNo ||
                        formData.phoneNo.trim() === '' ||
                        !formData.address ||
                        formData.address.trim() === '' ||
                        (!(isCompanyAdmin && isEdit) && (!formData.maxNoOfBranches || formData.maxNoOfBranches === '')) ||
                        (!(isCompanyAdmin && isEdit) && !formData.status)
                    }
                >
                    {isSubmitting ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
                </SoftButton>
            </DialogActions>
        </Dialog>
    );
};

AddEditCompanyDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    company: PropTypes.object,
    isEdit: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
};

export default AddEditCompanyDialog;

